import { fetchInboxIncremental, fetchInboxMessages } from "@/lib/api/imap";
import {
  isFolderBootstrapDone,
  markFolderBootstrapDone,
} from "@/lib/cache/folderBootstrapStore";
import { INBOX_BOOTSTRAP_SIZE, INBOX_LIST_SIZE, MAIL_STANDARD_FOLDER_MAX_PRIORITY } from "@/lib/constants/inbox";
import { persistInboxHeadersSession } from "@/lib/cache/inboxHeadersSessionCache";
import {
  getFolderSnapshot,
  isFolderSnapshotWarm,
  publishFolderSnapshot,
} from "@/lib/mail/inboxFolderBus";
import {
  notifyNewMailArrived,
  markPendingRevealBeforeInboxSnapshot,
} from "@/lib/mail/mailNewMailEvents";
import { filterHeldMutationMessages, filterTombstonedMessages, isUidTombstoned } from "@/lib/mail/mailMutationHold";
import { filterOutActiveSnoozeHiddenUids } from "@/lib/snooze/mailSnoozeActiveHide";
import type { MailFolder } from "@/lib/types/mail";
import type { MailMessageSummary } from "@/lib/types/mail";
import type { FolderSnapshot } from "@/lib/mail/inboxFolderBus";

type FolderKey = string;

const pendingKeys = new Set<FolderKey>();
const syncInflight = new Map<FolderKey, Promise<boolean>>();

function folderKey(accountId: number, folder: string): FolderKey {
  return `${accountId}\0${folder}`;
}

function markFolderWarmPending(accountId: number, folder: string): void {
  pendingKeys.add(folderKey(accountId, folder));
}

function clearFolderWarmPending(accountId: number, folder: string): void {
  pendingKeys.delete(folderKey(accountId, folder));
}

export function isFolderWarmPending(accountId: number, folder: string): boolean {
  return pendingKeys.has(folderKey(accountId, folder));
}

export function folderBootstrapPriority(name: string): number {
  const upper = name.toUpperCase();
  if (upper === "INBOX") return 0;
  if (name.startsWith("__")) return 10;
  if (/DRAFT|BROUILLON/.test(upper)) return 2;
  if (/JUNK|SPAM|INDÉSIR|INDESIR/.test(upper)) return 4;
  if (/TRASH|CORBEILLE|LIXEIRA|PAPIERKORB|CESTINO|PAPELERA|DELETED|SUPPRIM/.test(upper)) return 3;
  if (/(^|\/|\.)BIN($|\/|\.)/i.test(name)) return 3;
  if (/SENT|ENVOY/.test(upper) && !/INDÉSIR|INDESIR|SPAM|JUNK/.test(upper)) return 1;
  if (/ARCHIVE/i.test(name)) return 5;
  return 10;
}

export function isStandardBootstrapFolder(name: string): boolean {
  return folderBootstrapPriority(name) <= MAIL_STANDARD_FOLDER_MAX_PRIORITY;
}

export function sortFoldersForBootstrap(folders: MailFolder[]): MailFolder[] {
  return [...folders].sort(
    (a, b) =>
      folderBootstrapPriority(a.name) - folderBootstrapPriority(b.name) ||
      a.label.localeCompare(b.label, "fr")
  );
}

export function shouldSkipFolderWarm(accountId: number, folder: string): boolean {
  if (isFolderBootstrapDone(accountId, folder)) return true;

  const snap = getFolderSnapshot(accountId, folder);
  if (!isFolderSnapshotWarm(snap)) return false;
  if (!snap) return false;
  if (snap.bootstrapped && snap.messages.length >= Math.min(INBOX_BOOTSTRAP_SIZE, snap.total)) {
    markFolderBootstrapDone(accountId, folder, snap.messages.length);
    return true;
  }
  if (snap.messages.length >= INBOX_BOOTSTRAP_SIZE) {
    markFolderBootstrapDone(accountId, folder, snap.messages.length);
    return true;
  }
  if (snap.total > 0 && snap.messages.length >= snap.total) {
    markFolderBootstrapDone(accountId, folder, snap.messages.length);
    return true;
  }
  return false;
}

/**
 * Charge jusqu’à 200 headers en mémoire session + DD02 serveur.
 */
export function mergeBatchWithFolderSnapshot(
  accountId: number,
  folder: string,
  batch: MailMessageSummary[]
): MailMessageSummary[] {
  const existing = getFolderSnapshot(accountId, folder);
  if (!existing?.messages.length) return sortInboxMessageList(batch);
  return mergeHeaderLists(existing.messages, batch);
}

export async function warmFolderMessages(
  accountId: number,
  folder: string,
  limit = INBOX_BOOTSTRAP_SIZE
): Promise<boolean> {
  if (shouldSkipFolderWarm(accountId, folder)) {
    const existing = getFolderSnapshot(accountId, folder);
    if (!existing?.messages.length) {
      const res = await fetchInboxMessages({ accountId, folder, limit, offset: 0 });
      if (res.ok) {
        const batch = res.data.messages ?? [];
        const nextTotal = res.data.total ?? batch.length;
        if (batch.length > 0 || nextTotal === 0) {
          publishFolderSnapshot({
            accountId,
            folder,
            messages: mergeBatchWithFolderSnapshot(accountId, folder, batch),
            total: nextTotal,
            offset: batch.length,
            fromCache: res.data.cache === "hit",
            bootstrapped: true,
          });
        }
      }
    }
    const status = await syncFolderIncremental(accountId, folder, limit);
    if (status === "full") {
      return refreshFolderFromImap(accountId, folder, limit);
    }
    return true;
  }

  markFolderWarmPending(accountId, folder);

  try {
    const res = await fetchInboxMessages({
      accountId,
      folder,
      limit,
      offset: 0,
    });

    if (!res.ok) return false;

    const batch = res.data.messages ?? [];
    const nextTotal = res.data.total ?? batch.length;

    if (batch.length === 0 && nextTotal > 0) {
      return false;
    }

    publishFolderSnapshot({
      accountId,
      folder,
      messages: mergeBatchWithFolderSnapshot(accountId, folder, batch),
      total: nextTotal,
      offset: batch.length,
      fromCache: res.data.cache === "hit",
      bootstrapped: true,
    });

    await syncFolderIncremental(accountId, folder, limit);
    markFolderBootstrapDone(accountId, folder, batch.length);

    return true;
  } finally {
    clearFolderWarmPending(accountId, folder);
  }
}

export type FolderIncrementalSyncResult = "ok" | "full" | "fail";

export async function refreshFolderFromImap(
  accountId: number,
  folder: string,
  limit = INBOX_BOOTSTRAP_SIZE
): Promise<boolean> {
  const res = await fetchInboxMessages({
    accountId,
    folder,
    limit,
    offset: 0,
    refresh: true,
  });
  if (!res.ok) return false;

  const batch = res.data.messages ?? [];
  const nextTotal = res.data.total ?? batch.length;
  if (batch.length === 0 && nextTotal > 0) {
    return false;
  }

  publishFolderSnapshot({
    accountId,
    folder,
    messages: mergeBatchWithFolderSnapshot(accountId, folder, batch),
    total: nextTotal,
    offset: batch.length,
    fromCache: false,
    bootstrapped: true,
  });
  markFolderBootstrapDone(accountId, folder, batch.length);
  return true;
}

/**
 * Sync légère : IMAP uniquement pour les UIDs > highestUid en DD02.
 */
async function syncFolderIncremental(
  accountId: number,
  folder: string,
  limit = INBOX_BOOTSTRAP_SIZE
): Promise<FolderIncrementalSyncResult> {
  const res = await fetchInboxIncremental({ accountId, folder, limit });
  if (!res.ok) return "fail";

  if (res.data.needFullBootstrap) {
    return "full";
  }

  const existing = getFolderSnapshot(accountId, folder);
  const hadList = (existing?.messages.length ?? 0) > 0;
  const newMessages = res.data.newMessages ?? [];
  const newCount = res.data.newCount ?? newMessages.length;

  if (newCount === 0) {
    if (existing && res.data.total !== undefined && existing.total !== res.data.total) {
      publishFolderSnapshot({
        ...existing,
        total: res.data.total,
      });
    }
    return "ok";
  }

  const alreadyKnown = new Set((existing?.messages ?? []).map((m) => m.uid));
  const rawNewUids =
    newMessages.length > 0
      ? newMessages.map((m) => m.uid).filter((uid) => uid > 0)
      : [];
  /** Ignore les UIDs déjà poussés par le sync cache (évite 2ᵉ notify / 2ᵉ prep). */
  const freshUidsRaw =
    rawNewUids.length > 0
      ? rawNewUids.filter((uid) => !alreadyKnown.has(uid))
      : getNewMessageUids(existing?.messages, res.data.messages ?? newMessages);
  const freshUids = filterOutActiveSnoozeHiddenUids(accountId, folder, freshUidsRaw).filter(
    (uid) => !isUidTombstoned(accountId, folder, uid)
  );

  if (freshUids.length === 0) {
    if (existing && res.data.total !== undefined && existing.total !== res.data.total) {
      publishFolderSnapshot({
        ...existing,
        total: res.data.total,
      });
    }
    return "ok";
  }

  const serverMessages = reconcileSessionMessages(
    existing?.messages,
    res.data.messages?.length ? res.data.messages : newMessages,
    {
      previousTotal: existing?.total,
      nextTotal: res.data.total,
    }
  );
  const persistMessages = filterHeldMutationMessages(accountId, folder, serverMessages);
  const removedCount = serverMessages.length - persistMessages.length;
  const adjustedTotal = Math.max(0, (res.data.total ?? persistMessages.length) - removedCount);

  if (!hadList) {
    publishFolderSnapshot({
      accountId,
      folder,
      messages: persistMessages,
      total: adjustedTotal,
      offset: sessionOffsetAfterMerge(existing?.offset, persistMessages),
      fromCache: false,
      bootstrapped: true,
    });
    persistInboxHeadersSession(
      accountId,
      folder,
      persistMessages,
      adjustedTotal,
      sessionOffsetAfterMerge(existing?.offset, persistMessages)
    );
    markFolderBootstrapDone(accountId, folder, persistMessages.length);
    return "ok";
  }

  markPendingRevealBeforeInboxSnapshot(accountId, folder, persistMessages, freshUids);

  publishFolderSnapshot({
    accountId,
    folder,
    messages: persistMessages,
    total: adjustedTotal,
    offset: sessionOffsetAfterMerge(existing?.offset, persistMessages),
    fromCache: false,
    bootstrapped: true,
  });
  persistInboxHeadersSession(
    accountId,
    folder,
    persistMessages,
    adjustedTotal,
    sessionOffsetAfterMerge(existing?.offset, persistMessages)
  );

  notifyNewMailArrived(accountId, folder, freshUids.length, freshUids, {
    holdAlreadyApplied: true,
    pendingAlreadyApplied: true,
  });

  return "ok";
}

export function sortInboxMessageList(messages: MailMessageSummary[]): MailMessageSummary[] {
  return [...messages].sort((a, b) => {
    const aUnread = a.seen ? 0 : 1;
    const bUnread = b.seen ? 0 : 1;
    if (aUnread !== bUnread) return bUnread - aUnread;
    return b.uid - a.uid;
  });
}

/**
 * Fusion lu/non lu au merge headers.
 * Le serveur IMAP fait foi — un « non lu » posé dans Gmail doit redescendre.
 * L’optimisme clic local est géré par `pendingSeen` (TTL long) + sticky DD02.
 */
export function mergeSeenFlags(_existingSeen: boolean, serverSeen: boolean): boolean {
  return serverSeen;
}

/**
 * Fusionne les métadonnées sans réordonner les lignes déjà peintes.
 * Non-lus encore absents → en tête. Lus encore absents (backfill 50→100) → en bas.
 * Une 2ᵉ vague IMAP ne doit plus passer devant les non-lus comme « nouveau mail ».
 */
export function mergeInboxListsPreservingOrder(
  prev: MailMessageSummary[],
  fresh: MailMessageSummary[],
  keepUids?: ReadonlySet<number>
): MailMessageSummary[] {
  if (prev.length === 0) return sortInboxMessageList([...fresh]);
  if (fresh.length === 0 && (!keepUids || keepUids.size === 0)) return prev;

  const freshMap = new Map(fresh.map((m) => [m.uid, m]));
  const prevUidSet = new Set(prev.map((m) => m.uid));

  const kept = prev
    .map((m) => {
      const incoming = freshMap.get(m.uid);
      if (!incoming) return m;
      return {
        ...incoming,
        seen: mergeSeenFlags(m.seen, incoming.seen),
      };
    })
    .filter((m) => freshMap.has(m.uid) || (keepUids?.has(m.uid) ?? false));

  const newcomers = fresh.filter((m) => !prevUidSet.has(m.uid));
  const newUnread = newcomers.filter((m) => !m.seen).sort((a, b) => b.uid - a.uid);
  const newRead = newcomers.filter((m) => m.seen).sort((a, b) => b.uid - a.uid);

  return [...newUnread, ...kept, ...newRead];
}

export function mergeHeaderLists(
  cached: MailMessageSummary[],
  fresh: MailMessageSummary[]
): MailMessageSummary[] {
  const map = new Map<number, MailMessageSummary>();
  for (const msg of cached) {
    map.set(msg.uid, msg);
  }
  for (const msg of fresh) {
    const existing = map.get(msg.uid);
    if (existing) {
    map.set(msg.uid, {
      ...msg,
      seen: mergeSeenFlags(existing.seen, msg.seen),
      flagged: (existing.flagged ?? false) || (msg.flagged ?? false),
    });
    } else {
      map.set(msg.uid, msg);
    }
  }
  return sortInboxMessageList([...map.values()]);
}

/** Refresh IMAP : liste = batch serveur ; seen = serveur (pendingSeen appliqué ensuite dans le hook). */
export function mergeRefreshBatch(
  prev: MailMessageSummary[],
  batch: MailMessageSummary[]
): MailMessageSummary[] {
  const prevMap = new Map(prev.map((m) => [m.uid, m]));
  const next = batch.map((m) => {
    const existing = prevMap.get(m.uid);
    if (!existing) return m;
    return {
      ...m,
      seen: mergeSeenFlags(existing.seen, m.seen),
    };
  });
  return sortInboxMessageList(next);
}

/** Fusionne la fenêtre serveur avec la liste paginée locale sans tronquer. */
export function reconcileSessionMessages(
  existing: MailMessageSummary[] | undefined,
  incoming: MailMessageSummary[],
  options?: { previousTotal?: number; nextTotal?: number }
): MailMessageSummary[] {
  const nextTotal = options?.nextTotal;
  const previousTotal = options?.previousTotal;

  if (incoming.length === 0) {
    return nextTotal === 0 ? [] : (existing ?? []);
  }
  if (!existing?.length) {
    return sortInboxMessageList(incoming.slice(0, INBOX_LIST_SIZE));
  }
  if (
    nextTotal !== undefined &&
    previousTotal !== undefined &&
    nextTotal < previousTotal
  ) {
    return mergeHeaderLists(existing, incoming).slice(0, INBOX_LIST_SIZE);
  }
  if (incoming.length < existing.length) {
    const incomingUids = new Set(incoming.map((m) => m.uid));
    if (existing.some((m) => !incomingUids.has(m.uid))) {
      return mergeHeaderLists(existing, incoming).slice(0, INBOX_LIST_SIZE);
    }
  }
  return mergeHeaderLists(existing, incoming).slice(0, INBOX_LIST_SIZE);
}

/** Applique un snapshot bus sans restaurer des UIDs supprimés localement (cache DD02 périmé). */
export function mergeSnapshotWithLocal(
  prev: MailMessageSummary[],
  snap: Pick<FolderSnapshot, "accountId" | "folder" | "messages" | "fromCache" | "total">
): MailMessageSummary[] {
  const messages = filterTombstonedMessages(snap.accountId, snap.folder, snap.messages);
  if (prev.length === 0) return messages;
  if (messages.length === 0) {
    return snap.total === 0 ? messages : filterTombstonedMessages(snap.accountId, snap.folder, prev);
  }

  const prevUids = new Set(prev.map((m) => m.uid));
  const removedFromPrev = prev.filter((m) => !messages.some((row) => row.uid === m.uid));
  const hasLocalRemovals = removedFromPrev.length > 0 && prev.length > messages.length;

  const hasOverlap = messages.some((m) => prevUids.has(m.uid));
  if (!hasOverlap) {
    if (hasLocalRemovals) {
      const maxPrevUid = Math.max(...prev.map((m) => m.uid));
      const genuinelyNew = messages.filter((m) => m.uid > maxPrevUid);
      if (genuinelyNew.length === 0) {
        return filterTombstonedMessages(snap.accountId, snap.folder, prev);
      }
      return mergeHeaderLists(prev, genuinelyNew);
    }
    return messages;
  }
  const minPrevUid = Math.min(...prev.map((m) => m.uid));
  const maxPrevUid = Math.max(...prev.map((m) => m.uid));

  if (snap.fromCache) {
    const reInserted = messages.some(
      (m) => !prevUids.has(m.uid) && m.uid >= minPrevUid
    );
    if (reInserted) {
      const genuinelyNew = messages.filter((m) => m.uid > maxPrevUid);
      if (genuinelyNew.length === 0) {
        return filterTombstonedMessages(snap.accountId, snap.folder, prev);
      }
      return mergeHeaderLists(prev, genuinelyNew);
    }
  }

  if (prev.length > messages.length) {
    if (prev.length > INBOX_LIST_SIZE) {
      const genuinelyNew = messages.filter((m) => !prevUids.has(m.uid) && m.uid > maxPrevUid);
      if (genuinelyNew.length === 0) {
        return filterTombstonedMessages(snap.accountId, snap.folder, prev);
      }
      return mergeHeaderLists(prev, genuinelyNew);
    }
    const snapMinUid =
      messages.length > 0
        ? Math.min(...messages.map((m) => m.uid))
        : Number.POSITIVE_INFINITY;
    const older = prev.filter((m) => m.uid < snapMinUid);
    return mergeHeaderLists(older, messages).slice(0, INBOX_LIST_SIZE);
  }

  return mergeHeaderLists(prev, messages);
}

function sessionOffsetAfterMerge(
  prevOffset: number | undefined,
  nextMessages: MailMessageSummary[]
): number {
  return Math.max(prevOffset ?? 0, nextMessages.length);
}

function countNewMessages(
  existing: MailMessageSummary[] | undefined,
  fresh: MailMessageSummary[]
): number {
  return getNewMessageUids(existing, fresh).length;
}

function getNewMessageUids(
  existing: MailMessageSummary[] | undefined,
  fresh: MailMessageSummary[]
): number[] {
  if (!existing?.length) {
    return [];
  }
  const known = new Set(existing.map((m) => m.uid));
  return fresh.filter((m) => m.uid > 0 && !known.has(m.uid)).map((m) => m.uid);
}

export type ServerCacheSyncResult = "hit" | "miss" | "fail";

/**
 * Lit DD02 via API (sans refresh IMAP) — léger quand le cron a déjà sync.
 */
export async function syncFolderFromServerCache(
  accountId: number,
  folder: string,
  limit = INBOX_BOOTSTRAP_SIZE
): Promise<ServerCacheSyncResult> {
  const res = await fetchInboxMessages({ accountId, folder, limit, offset: 0 });
  if (!res.ok) return "fail";

  const batch = res.data.messages ?? [];
  const existing = getFolderSnapshot(accountId, folder);
  const newCount = countNewMessages(existing?.messages, batch);
  const nextTotal = res.data.total ?? batch.length;

  const changed =
    newCount > 0 ||
    batch.length !== (existing?.messages.length ?? 0) ||
    nextTotal !== (existing?.total ?? 0);

  if (batch.length === 0 && nextTotal > 0) {
    return "miss";
  }

  if (changed || !existing) {
    const nextMessages = reconcileSessionMessages(existing?.messages, batch, {
      previousTotal: existing?.total,
      nextTotal,
    });
    const persistMessages = filterHeldMutationMessages(accountId, folder, nextMessages);
    const removedCount = nextMessages.length - persistMessages.length;
    const adjustedTotal = Math.max(0, nextTotal - removedCount);

    if (persistMessages.length === 0 && nextTotal > 0) {
      return "miss";
    }

    const newUids = filterOutActiveSnoozeHiddenUids(
      accountId,
      folder,
      getNewMessageUids(existing?.messages, batch)
    ).filter((uid) => !isUidTombstoned(accountId, folder, uid));
    const mergedNewUids = filterOutActiveSnoozeHiddenUids(
      accountId,
      folder,
      newUids.length > 0 ? newUids : getNewMessageUids(existing?.messages, nextMessages)
    ).filter((uid) => !isUidTombstoned(accountId, folder, uid));

    const revealUids =
      newUids.length > 0 ? newUids : newCount > 0 ? mergedNewUids : [];
    if (revealUids.length > 0) {
      markPendingRevealBeforeInboxSnapshot(accountId, folder, persistMessages, revealUids);
    }

    publishFolderSnapshot({
      accountId,
      folder,
      messages: persistMessages,
      total: adjustedTotal,
      offset: sessionOffsetAfterMerge(existing?.offset, persistMessages),
      fromCache: res.data.cache === "hit" || res.data.cache === "partial-hit",
      bootstrapped: true,
    });
    persistInboxHeadersSession(
      accountId,
      folder,
      persistMessages,
      adjustedTotal,
      sessionOffsetAfterMerge(existing?.offset, persistMessages)
    );
    if (newUids.length > 0) {
      notifyNewMailArrived(accountId, folder, newUids.length, newUids, {
        holdAlreadyApplied: true,
        pendingAlreadyApplied: true,
      });
    } else if (newCount > 0 && mergedNewUids.length > 0) {
      notifyNewMailArrived(accountId, folder, mergedNewUids.length, mergedNewUids, {
        holdAlreadyApplied: true,
        pendingAlreadyApplied: true,
      });
    }
  }

  const result: ServerCacheSyncResult =
    res.data.cache === "hit" || res.data.cache === "partial-hit" ? "hit" : "miss";

  // Pas d’hydratation corps → IndexedDB : ouverture = lecture directe DD02.
  return result;
}

/**
 * Resync discrète — sync incrémentale DD02 (nouveaux UIDs seulement).
 */
export async function syncFolderHeaders(
  accountId: number,
  folder: string,
  limit = INBOX_BOOTSTRAP_SIZE
): Promise<boolean> {
  const key = folderKey(accountId, folder);
  const existing = syncInflight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<boolean> => {
    const status = await syncFolderIncremental(accountId, folder, limit);
    if (status === "ok") return true;
    if (status === "full") return warmFolderMessages(accountId, folder, limit);
    return false;
  })();

  syncInflight.set(key, promise);
  try {
    return await promise;
  } finally {
    syncInflight.delete(key);
  }
}

export function idleDelay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => resolve(), { timeout: ms + 500 });
    } else {
      window.setTimeout(resolve, ms);
    }
  });
}
